import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, DatePicker, Empty, List, Popconfirm, Space, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import { useState } from 'react';
import { getApiError } from '../../api/client';
import type { CompanyVatPeriod } from '../../api/types';
import { formatDate } from '../../lib/format';
import { createVatPeriod, deleteVatPeriod } from './api';

const { Text } = Typography;

function periodLabel(period: CompanyVatPeriod): string {
  return period.endsOn ? `${formatDate(period.startsOn)} – ${formatDate(period.endsOn)}` : `din ${formatDate(period.startsOn)}`;
}

/**
 * "TVA" tab of the FirmaFormDrawer (edit mode only). Reads its list straight
 * off the parent's `GET /firms/:id` fetch (`FirmDetail.vatPeriods`) rather
 * than a separate query — add/delete both invalidate `['firms', 'detail',
 * firmaId]`, which refetches that same detail query and flows the updated
 * array back down as a prop.
 */
export function FirmaVatPeriodsTab({ firmaId, periods }: { firmaId: string; periods: CompanyVatPeriod[] }) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [startsOn, setStartsOn] = useState<Dayjs | null>(null);
  const [endsOn, setEndsOn] = useState<Dayjs | null>(null);

  // Invalidates the whole `['firms', ...]` branch, not just this firm's
  // detail query: a VAT period add/delete changes `isVatPayerToday`, which
  // is also shown on FirmeListPage's table and read by FirmaCurentaContext
  // (['firms'], pageSize 100) for the header selector — both need to refetch,
  // not just the drawer's own detail query.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['firms'] });

  const addMutation = useMutation({
    mutationFn: () =>
      createVatPeriod(firmaId, {
        startsOn: startsOn!.format('YYYY-MM-DD'),
        endsOn: endsOn ? endsOn.format('YYYY-MM-DD') : undefined,
      }),
    onSuccess: () => {
      message.success('Perioada de TVA a fost adăugată.');
      setStartsOn(null);
      setEndsOn(null);
      invalidate();
    },
    onError: (error) => {
      // 409 VAT_PERIOD_OVERLAP has no field-level `details` — the DB's
      // EXCLUDE constraint doesn't know which of the two dates to blame —
      // so this always surfaces as the server's Romanian message in a toast.
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Perioada de TVA nu a putut fi adăugată.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (periodId: string) => deleteVatPeriod(firmaId, periodId),
    onSuccess: () => {
      message.success('Perioada de TVA a fost ștearsă.');
      invalidate();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Perioada de TVA nu a putut fi ștearsă.');
    },
  });

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <DatePicker placeholder="De la" value={startsOn} onChange={setStartsOn} format="DD.MM.YYYY" />
        <DatePicker
          placeholder="Până la (opțional)"
          value={endsOn}
          onChange={setEndsOn}
          format="DD.MM.YYYY"
          disabledDate={(d) => (startsOn ? d.isBefore(startsOn, 'day') : false)}
        />
        <Button type="primary" disabled={!startsOn} loading={addMutation.isPending} onClick={() => addMutation.mutate()}>
          Adaugă
        </Button>
      </Space>
      {periods.length === 0 ? (
        <Empty description="Nicio perioadă de TVA înregistrată." />
      ) : (
        <List
          dataSource={periods}
          renderItem={(period) => (
            <List.Item
              actions={[
                <Popconfirm key="del" title="Ștergi perioada?" onConfirm={() => deleteMutation.mutate(period.id)}>
                  <Button size="small" danger loading={deleteMutation.isPending && deleteMutation.variables === period.id}>
                    Șterge
                  </Button>
                </Popconfirm>,
              ]}
            >
              <Text>{periodLabel(period)}</Text>
            </List.Item>
          )}
        />
      )}
    </div>
  );
}
